import {MemberAssignment} from "./memberAssignment";
import {CategoryAssignment} from "./categoryAssignment";
import {Seniority} from "./seniority";
import {LanguageAssignment} from "./languageAssignment";
import {DegreeAssignment} from "./degreeAssignment";
import {PasswordReset} from "./password-reset";
import {FileEntry} from "./fileEntry";
import { Holiday } from "../controls/leave-manager/leave-manager.component";
import {Capacity} from "../controls/capacity-manager/capacity-manager.component";
import {Project} from "./project";
import {Client} from "./client";
import {MarketAssignment} from "./marketAssignment";
import {QuestionAssignment} from "./questionAssignment";
import {IndustryAssignment} from "./industryAssignment";
import {DomainAssignment} from "./domainAssignment";
import {Department} from "./department";
import {DevelopmentAssignment} from "./developmentAssignment";
import {SkillAssignment} from "./skill";
import {CertificationAssignment} from "./certification";
import {TrackedTime} from "../shared/time-tracking.service";
import {FormGroup} from "@angular/forms";

export interface Searchable{
  id: number,
  name?: string,
  logo?: FileEntry
}

export const salutations = [
  'mr',
  'miss',
  'mrs',
  'ms',
  'dr',
  'sir',
  'madam',
  'prof'
];

export enum UserStatus {
  INCOMPLETE = -1,
  ARCHIVED = 0,
  ACTIVE = 1,
  PENDING = 2
}

export enum UserRole{
  NA,
  USER,
  STAFFER,
  ADMIN
}

export enum UserGender {
  UNKNOWN = 'unknown',
  FEMALE = 'female',
  MALE = 'male'
}

/** A user as /users/lookup returns it, for pickers. */
export type UserRef = Pick<User, 'id' | 'name_first' | 'name_last'>;

export interface User extends Searchable{
  /** Set by /me: may open hours overviews and invoices (#1513). */
  can_invoice?: boolean,
  /** Set by /me: may create and change quotes (#1697). */
  can_quote?: boolean,
  username: string,
  name_first: string,
  name_last: string,
  name_middle: string,
  linkedin_url?: string,
  profile_text?: string,
  location_home: string,
  phone?: string,
  can_travel: boolean,
  last_login: Date,
  created_at: Date,
  updated_at: Date,
  company: string,
  gender: UserGender,
  gender_other: string,
  salutation: string,
  birthdate: Date|string,
  capacity: Capacity,
  upcoming_capacity?: Capacity,
  assignments: MemberAssignment[],
  career: any,
  categories: CategoryAssignment[],
  industries: IndustryAssignment[],
  domains: DomainAssignment[],
  languages: LanguageAssignment[],
  markets: MarketAssignment[],
  degrees: DegreeAssignment[],
  developments: DevelopmentAssignment[];
  skills: SkillAssignment[];
  certifications: CertificationAssignment[];
  holidays?: Holiday[],
  provider_id: number,
  provider: string,
  provider_avatar: string,
  provider_username: string,
  key?: string,
  has_password?: boolean,
  is_super_user?: boolean,
  crm_enabled?: boolean,
  trips_enabled?: boolean,
  invoicing_enabled?: boolean,
  dashboard_enabled?: boolean,
  intake_enabled?: boolean,
  quotes_enabled?: boolean,
  passwords?: PasswordReset[],
  email_verified_at: Date,
  file_entries: FileEntry[],
  projects?: Project[],
  client?: Client,
  clients?: Client[],
  user_to_client: UserToClient,
  tracking?: TrackedTime,
  analytics_opt_out?: boolean,
  notify_time_approval?: boolean,
  notify_project_updates?: boolean,
  notify_review?: boolean,
  notify_lifecycle?: boolean,
  billable_actual?: number | null,
  delegates?: { id: number; name: string }[],
  auto_approve_time?: boolean,
  can_configure_auto_approve?: boolean,
  _calculated?: any,
}

export interface UserToClient{
  onboarded_at: Date,
  start_on: Date|string,
  end_on: Date|string,
  role: UserRole,
  freelancer: boolean,
  visible_in_environment?: boolean,
  exclude_from_matching?: boolean,
  cost: number,
  offered: number,
  billable_target?: number | null,
  view_bill_rate?: boolean | null,
  view_cost?: boolean | null,
  view_margin?: boolean | null,
  grant_invoicing?: boolean | null,
  grant_quotes?: boolean | null,
  vehicle?: 'own' | 'lease' | 'none',
  status: UserStatus,
  seniority: Seniority,
  department: Department,
  Seniority_id: number,
  invited_at: Date,
  invites: number,
  reviewed_at: Date,
  note?: string,
  parent?: User,
  parent_id?: number,
  questions: QuestionAssignment[],
  created_at?: number|Date,
  created_by?: string|User,
  updated_at?: number|Date,
  archived_at?: number|Date,
  archived_by?: string|User
}

export interface UserResult extends Searchable{
  user: User,
  capacity_updated: number,
  score: number,
  offered?: number,
  cost?: number,
  ProjectRole_id?: number,
  highlights?: string[],
  open: boolean,
  form?: FormGroup
}

export interface TrackUserResult extends Searchable{
  subtitle: string,
  provider_avatar: string,
  username: string
}
